import React, { useRef } from 'react';
import PropTypes from 'prop-types';

/**
 * One picture on a facility: its stored value, what it looks like, and the two
 * buttons that change it.
 *
 * The stored value is not a URL — it is an image key that the static host
 * resolves, except when somebody has pasted an absolute URL, which the old page
 * also allows. `imageSrc` keeps both cases working.
 *
 * Uploading goes through the crop step: the file is read locally, the parent
 * opens the cropper with the aspect ratio this slot needs, and only the cropped
 * result is sent. That is the old flow exactly, because the ratios are what make
 * the images usable on the site — a square logo cropped as a banner is a bug
 * nobody notices until it is live.
 *
 * "Generate preview" is a separate step and only some slots have one: it derives
 * the small version from the picture already stored and saves it under its own
 * key.
 */

const STATIC = 'https://static.wannasport.dk/images/';

export const imageSrc = (value, suffix = '') => {
  if (!value) return null;
  const text = String(value);
  if (text.startsWith('data:image/') || text.startsWith('http')) return text;
  return `${STATIC}${text}${suffix}`;
};

export default function ImageSlot({
  label,
  value,
  onChange,
  onPick,
  ratio,
  hint,
  previewValue,
  previewLabel,
  onGeneratePreview,
  busy
}) {
  const fileRef = useRef(null);
  const src = imageSrc(value);
  const previewSrc = imageSrc(previewValue);

  return (
    <section className="next-imageslot">
      <div className="next-imageslot__head">
        <span className="next-filter__label">{label}</span>
        {hint && <span className="next-form__hint">{hint}</span>}
      </div>

      <div className="next-imageslot__body">
        <div className="next-imageslot__thumb">
          {src
            ? <img src={src} alt="" />
            : <span className="next-muted">No image</span>}
        </div>

        <div className="next-imageslot__fields">
          <input
            className="next-input"
            value={value || ''}
            placeholder="Image key or URL"
            onChange={event => onChange(event.target.value)}
          />

          <div className="next-imageslot__actions">
            <button
              type="button"
              className="next-btn next-btn--sm"
              disabled={busy}
              onClick={() => fileRef.current?.click()}
            >
              Upload and crop…
            </button>

            {onGeneratePreview && (
              <button
                type="button"
                className="next-btn next-btn--sm"
                disabled={!value || busy}
                title={value ? undefined : 'Upload a picture first'}
                onClick={onGeneratePreview}
              >
                Generate preview
              </button>
            )}

            <input
              ref={fileRef}
              className="next-imageslot__file"
              type="file"
              accept="image/*"
              onChange={event => {
                const file = event.target.files && event.target.files[0];
                if (file) onPick(file, ratio);
                // Cleared so picking the same file twice still fires a change.
                event.target.value = '';
              }}
            />
          </div>

          {previewLabel && (
            <label className="next-imageslot__preview">
              <span className="next-filter__label">{previewLabel}</span>
              {/* Read-only: it is written by "Generate preview", and typing a key
                  here would point at something nobody generated. */}
              <input className="next-input" value={previewValue || ''} readOnly />
              {previewSrc && <img src={previewSrc} alt="" />}
            </label>
          )}
        </div>
      </div>
    </section>
  );
}

ImageSlot.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onPick: PropTypes.func.isRequired,
  ratio: PropTypes.number.isRequired,
  hint: PropTypes.string,
  previewValue: PropTypes.string,
  previewLabel: PropTypes.string,
  onGeneratePreview: PropTypes.func,
  busy: PropTypes.bool
};
